
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route, Navigate } from "react-router-dom";
import { useEffect } from "react";
import MainLayout from "./components/layout/MainLayout";
import Dashboard from "./pages/Dashboard";
import Orders from "./pages/Orders";
import Tracking from "./pages/Tracking";
import Profile from "./pages/Profile";
import Reconciliation from "./pages/Reconciliation";
import Reports from "./pages/Reports";
import Support from "./pages/Support";
import Customers from "./pages/Customers";
import InternalAccounts from "./pages/InternalAccounts";
import Hubs from "./pages/Hubs";
import Coverage from "./pages/Coverage";
import Services from "./pages/Services";
import Trips from "./pages/Trips";
import RoutesManagement from "./pages/Routes";
import TransitBags from "./pages/TransitBags";
import Warehouse from "./pages/Warehouse";
import NotFound from "./pages/NotFound";
import Login from "./pages/Login";
import Unauthorized from "./pages/Unauthorized";
import LanguageTest from "./pages/LanguageTest";
import PriceConfiguration from "./pages/PriceConfiguration";
import TripManagement from "./pages/TripManagement";
import ActivityLog from "./pages/ActivityLog";
import CallHistory from "./pages/CallHistory";
import CODCollection from "./pages/CODCollection";
import ThirdPartyAPI from "./pages/ThirdPartyAPI";
import Settings from "./pages/Settings";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import { requestNotificationPermission, onMessageListener } from "./lib/firebase";
import { toast } from "@/hooks/use-toast";
import { isAuthenticated } from "./lib/utils";

const queryClient = new QueryClient();

const App = () => {
  useEffect(() => {
    // Request notification permission when app loads
    const setupNotifications = async () => {
      try {
        // Kiểm tra xem có Firebase config hợp lệ không
        if (!import.meta.env.VITE_FIREBASE_API_KEY || import.meta.env.VITE_FIREBASE_API_KEY === "your_firebase_api_key_here") {
          console.warn("Firebase API key not configured. Notifications will be disabled.");
          return;
        }

        // Yêu cầu quyền thông báo và lấy token (nhưng không gửi lên server ngay)
        const token = await requestNotificationPermission();
        if (token) {
          console.log("Notification permission granted. Token ready for use after login.");
        }
        
        // Thiết lập bộ lắng nghe tin nhắn foreground
        const payload = await onMessageListener();
        if (payload) {
          toast({
            title: payload.notification?.title || "New Notification",
            description: payload.notification?.body || "",
            duration: 5000,
          });
        }
      } catch (err) {
        console.error("Failed to set up notification:", err);
        // Hiển thị thông báo lỗi cho người dùng
        if (err instanceof Error && err.message.includes("API key not valid")) {
          toast({
            title: "Cấu hình Firebase không hợp lệ",
            description: "Vui lòng kiểm tra cấu hình Firebase API key.",
            variant: "destructive",
            duration: 5000,
          });
        }
      }
    };
    
    // Thiết lập thông báo
    setupNotifications();
    
    // Thiết lập bộ lắng nghe tin nhắn liên tục
    const intervalId = setInterval(async () => {
      try {
        const payload = await onMessageListener();
        if (payload) {
          toast({
            title: payload.notification?.title || "New Notification",
            description: payload.notification?.body || "",
            duration: 5000,
          });
        }
      } catch (err) {
        console.error("Failed to listen for messages:", err);
      }
    }, 10000); // Kiểm tra mỗi 10 giây
    
    return () => {
      clearInterval(intervalId);
    };
    
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <Routes>
          {/* Trang công khai */}
          <Route path="/login" element={
            localStorage.getItem('user') ? <Navigate to="/" replace /> : <Login />
          } />
          <Route path="/unauthorized" element={<Unauthorized />} />
          
          {/* Trang được bảo vệ - Yêu cầu đăng nhập */}
          <Route element={<ProtectedRoute allowedRoles={["admin", "manager", "staff"]} />}>
            <Route path="/" element={<MainLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="orders" element={<Orders />} />
              <Route path="tracking" element={<Tracking />} />
              <Route path="profile" element={<Profile />} />
              <Route path="reconciliation" element={<Reconciliation />} />
              <Route path="reports" element={<Reports />} />
              <Route path="support" element={<Support />} />
              <Route path="customers" element={<Customers />} />
              <Route path="internal-accounts" element={<InternalAccounts />} />
              <Route path="hubs" element={<Hubs />} />
              <Route path="coverage" element={<Coverage />} />
              <Route path="services" element={<Services />} />
              <Route path="trips" element={<RoutesManagement />} />
              <Route path="trip-management" element={<TripManagement />} />
              <Route path="transit-bags" element={<TransitBags />} />
              <Route path="warehouse" element={<Warehouse />} />
              <Route path="price-configuration" element={<PriceConfiguration />} />
              <Route path="activity-log" element={<ActivityLog />} />
              <Route path="call-history" element={<CallHistory />} />
              <Route path="cod-collection" element={<CODCollection />} />
              <Route path="third-party-api" element={<ThirdPartyAPI />} />
              <Route path="language-test" element={<LanguageTest />} />
              <Route path="settings" element={<Settings />} />
            </Route>
          </Route>

          {/* 404 - Không tìm thấy trang */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
